import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatStepDuration } from '@arthelper/shared/quests/step-catalog';
import { useMutation, useQuery } from '@apollo/client';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { questPointsEarned } from '@arthelper/shared/quests/ledger-points';
import { questPaletteFor } from '@arthelper/shared/quests/quest-palette';
import { questTypeEmoji } from '@arthelper/shared/quests/quest-types';
import {
  COMPLETE_QUEST_STEP,
  MY_QUESTS,
  RETIRE_QUEST,
  UNCOMPLETE_QUEST_STEP,
  type ScoredQuest,
  type ScoredQuestStep,
} from './quest-queries';
import './quest-band.css';
import { recurringPeriodLabel } from '@arthelper/shared/recurrence/period-label';

const MINIMIZED_KEY = 'quest-band:minimized';

/**
 * No stored choice yet: phones start COLLAPSED (Nick, 2026-10-10) — the open card took
 * most of a phone's first screen. Once she expands or minimizes, her choice wins.
 */
function readMinimized(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const stored = window.localStorage.getItem(MINIMIZED_KEY);
    if (stored !== null) return stored === '1';
    return window.matchMedia('(max-width: 639px)').matches;
  } catch {
    return false;
  }
}

function writeMinimized(value: boolean) {
  try {
    window.localStorage.setItem(MINIMIZED_KEY, value ? '1' : '0');
  } catch {
    // storage unavailable — the band just won't remember
  }
}

const TOWN_SQUARE = (typeof __TOWN_SQUARE_URL__ !== 'undefined' && __TOWN_SQUARE_URL__) || 'https://www.arthelper.com';

function motionOk(): boolean {
  return typeof window !== 'undefined' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * THE QUEST BAND, client-app edition (Nick, 2026-09-01: the checklist appears on
 * every arthelper.ai page, plus town-square's community surfaces). A deliberate LITE
 * port of town-square's QuestBand — same serving order (myQuests[0]), same checkbox
 * semantics, same animated points counter — minus the surfaces that live on
 * town-square (invite modal, buddies, completion dialog), which the Invite link and
 * quest links reach cross-domain instead.
 *
 * Step navigation maps to THIS app: the add-artwork step goes to /my-artwork (it IS
 * this app); external tools open new tabs; town-square-relative hrefs cross domains.
 */

/**
 * "Your quest continues ..." — in her clock, not ours. Duplicated rather than shared with
 * town-square's band: `shared/` is framework-free and these two apps do not import each
 * other, the same trade every other component pair across them already makes.
 */
function dayResumeLine(dayUnlocksAt: string | null | undefined): string {
  if (!dayUnlocksAt) return 'Your quest continues tomorrow.';
  const at = new Date(dayUnlocksAt);
  if (Number.isNaN(at.getTime())) return 'Your quest continues tomorrow.';
  // THE UNLOCK IS ALWAYS HER NEXT LOCAL MIDNIGHT, so printing the clock gave
  // "Monday 12:00 AM" — accurate, and it read like a scheduling error rather than a
  // day boundary (Nick, 2026-09-27). At midnight the DAY is the whole answer; the
  // timed form is kept for any unlock that is not on the hour, which the current
  // gate never produces but a future one might.
  const atMidnight = at.getHours() === 0 && at.getMinutes() === 0;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (atMidnight) {
    if (at.toDateString() === tomorrow.toDateString()) return 'Your quest continues tomorrow.';
    return `Your quest continues ${at.toLocaleDateString(undefined, { weekday: 'long' })}.`;
  }
  const sameDay = at.toDateString() === new Date().toDateString();
  const when = at.toLocaleString(undefined, {
    ...(sameDay ? {} : { weekday: 'long' }),
    hour: 'numeric',
    minute: '2-digit',
  });
  return `Your quest continues ${when}.`;
}

export function ClientQuestBand() {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { enabled, loading: flagLoading } = useFeatureFlag('artist_standard_prototype');
  const { data, loading, refetch } = useQuery<{ myQuests: ScoredQuest[] }>(MY_QUESTS, {
    skip: !isSignedIn || flagLoading || !enabled,
    fetchPolicy: 'cache-and-network',
  });
  const [completeStepMutation] = useMutation(COMPLETE_QUEST_STEP);
  const [uncompleteStepMutation] = useMutation(UNCOMPLETE_QUEST_STEP);
  const [retireQuestMutation] = useMutation(RETIRE_QUEST);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Minimized state survives page-to-page and reloads (Nick, 2026-09-01).
  const [minimized, setMinimized] = useState<boolean>(() => readMinimized());
  const trackRef = useRef<HTMLDivElement | null>(null);

  const quest = useMemo(() => {
    // An unpinned quest lives on the quests page only — its admin unticked "pin to top".
    const quests = (data?.myQuests ?? []).filter((candidate) => !candidate.complete && candidate.pinned !== false);
    return quests[0] ?? null;
  }, [data]);

  const nextStep = useMemo(
    () => (quest ? (quest.steps.find((step) => step.code === quest.nextStepCode) ?? null) : null),
    [quest]
  );
  const periodLabel = quest
    ? recurringPeriodLabel(quest.cadence, quest.periodEndsAt, undefined, quest.periodTimeZone ?? undefined)
    : null;

  const pointsEarned = useMemo(
    () => (quest ? questPointsEarned(quest.steps, { complete: quest.complete, pointsReward: quest.pointsReward }) : 0),
    [quest]
  );

  // The same up/down tween as the town-square band. First observation of a quest
  // seeds without firing so a page that loads already at a score does not pulse.
  const [displayPoints, setDisplayPoints] = useState(0);
  const [pointsPulse, setPointsPulse] = useState(false);
  const [seededSlug, setSeededSlug] = useState<string | null>(null);
  const tweenRef = useRef<number | null>(null);
  if (quest && seededSlug !== quest.slug) {
    setSeededSlug(quest.slug);
    setDisplayPoints(pointsEarned);
    setPointsPulse(false);
    if (tweenRef.current !== null) {
      cancelAnimationFrame(tweenRef.current);
      tweenRef.current = null;
    }
  }
  useEffect(() => {
    if (!motionOk()) {
      setDisplayPoints(pointsEarned);
      return;
    }
    const from = displayPoints;
    const to = pointsEarned;
    if (from === to) return;
    setPointsPulse(true);
    const started = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - started) / 600);
      const eased = 1 - (1 - t) ** 3;
      setDisplayPoints(Math.round(from + (to - from) * eased));
      if (t < 1) {
        tweenRef.current = requestAnimationFrame(tick);
      } else {
        setPointsPulse(false);
      }
    };
    if (tweenRef.current !== null) cancelAnimationFrame(tweenRef.current);
    tweenRef.current = requestAnimationFrame(tick);
    return () => {
      if (tweenRef.current !== null) cancelAnimationFrame(tweenRef.current);
    };
  }, [pointsEarned]); // deliberately not depending on displayPoints — it is the tween's own output

  const stepBurst = useCallback(() => {
    if (!motionOk()) return;
    const rect = trackRef.current?.getBoundingClientRect();
    const origin = rect
      ? { x: (rect.left + rect.width / 2) / window.innerWidth, y: (rect.top + rect.height / 2) / window.innerHeight }
      : undefined;
    void confetti({ particleCount: 24, spread: 55, startVelocity: 18, scalar: 0.7, origin });
  }, []);

  const openStep = useCallback(
    (step: ScoredQuestStep) => {
      if (step.completion === 'click' && !step.complete) {
        void completeStepMutation({ variables: { stepCode: step.code, source: 'click' } }).then(() => {
          stepBurst();
          void refetch();
        });
      }
      if (step.opens === 'artwork-modal') {
        // Adding artwork IS this app.
        navigate('/my-artwork');
        return;
      }
      if (step.opens === 'new-tab' || /^https?:/.test(step.href)) {
        window.open(
          step.href.startsWith('/') ? `${TOWN_SQUARE}${step.href}` : step.href,
          '_blank',
          'noopener,noreferrer'
        );
        return;
      }
      window.open(`${TOWN_SQUARE}${step.href}`, '_blank', 'noopener,noreferrer');
    },
    [completeStepMutation, navigate, refetch, stepBurst]
  );

  const toggleStep = useCallback(
    (step: ScoredQuestStep) => {
      if (step.completedBy === 'measured') return;
      const checking = !step.complete;
      const mutation = checking
        ? completeStepMutation({ variables: { stepCode: step.code, source: 'manual' } })
        : uncompleteStepMutation({ variables: { stepCode: step.code } });
      void mutation.then(() => {
        if (checking) stepBurst();
        void refetch();
      });
    },
    [completeStepMutation, uncompleteStepMutation, refetch, stepBurst]
  );

  if (!isSignedIn || flagLoading || !enabled || loading || !quest) return null;

  // The quest's chosen pastel (admin), or its stable slug-hashed one.
  const palette = questPaletteFor(quest.slug, quest.color);

  if (minimized) {
    return (
      <div className="as-skin as-bar-wrap" data-skin="brand" data-testid="client-quest-band">
        <div
          className="as-bar-mini"
          style={{ background: palette.background, borderColor: palette.borderColor }}
          data-testid="quest-band-mini"
          role="button"
          tabIndex={0}
          onClick={() => {
            setMinimized(false);
            writeMinimized(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setMinimized(false);
              writeMinimized(false);
            }
          }}
        >
          <p className="as-bar-mini-heading">{quest.heading}</p>
          <span className="as-bar-mini-track" aria-hidden="true">
            <span className="as-bar-mini-fill" style={{ width: `${quest.score}%` }} />
          </span>
          <span className="as-bar-mini-score">{quest.score}%</span>
          {/* The minimized band is the every-day reminder — it rides the top of the
              screen everywhere, so the day count has to survive the collapse (Nick,
              2026-09-26). Abbreviated because this row is a strip, not a card. */}
          {quest.totalDays > 1 && !quest.complete ? (
            <span className="as-bar-mini-days">
              Day {quest.currentDay}/{quest.totalDays}
            </span>
          ) : null}
          <span className="as-bar-mini-points">⭐ {pointsEarned.toLocaleString()} pts</span>
          <span className="as-bar-mini-expand" data-testid="quest-band-restore">
            Expand
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M4 6l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="as-skin as-bar-wrap" data-skin="brand" data-testid="client-quest-band">
      <span className="as-bar-kicker" data-testid="quest-band-kicker" aria-hidden="true">
        Quest
      </span>
      <section
        className="as-bar"
        aria-labelledby="client-quest-heading"
        style={{ background: palette.background, borderColor: palette.borderColor }}
      >
        <div className="as-bar-top">
          <div className="as-bar-title">
            <span
              className="as-quest-tile"
              data-qtype={quest.questType ?? 'general'}
              style={{ background: palette.tile }}
              aria-hidden="true"
            >
              {quest.emoji || questTypeEmoji(quest.questType)}
            </span>
            <p id="client-quest-heading" className="as-bar-heading">
              {quest.heading}
            </p>
            <button
              type="button"
              className="as-bar-leave"
              data-testid="quest-band-leave"
              onClick={() => setLeaveConfirmOpen(true)}
            >
              Leave
            </button>
          </div>
          <span className="as-bar-score">{quest.score}%</span>
        </div>
        {leaveConfirmOpen ? (
          <div
            className="as-leave-pop"
            role="alertdialog"
            aria-label="Leave this quest?"
            data-testid="quest-band-leave-confirm"
          >
            <p className="as-leave-pop-title">Leave this quest?</p>
            <p className="as-leave-pop-text">
              {quest.pointsReward > 0
                ? `You'll give up the ${quest.pointsReward.toLocaleString()} points for completing it — and you're already at ${quest.score}%. `
                : `You're already at ${quest.score}%. `}
              Most steps take just a few minutes.
            </p>
            <div className="as-leave-pop-actions">
              <button type="button" className="as-leave-pop-keep" onClick={() => setLeaveConfirmOpen(false)}>
                Keep going
              </button>
              <button
                type="button"
                className="as-leave-pop-go"
                data-testid="quest-band-leave-go"
                onClick={() => {
                  setLeaveConfirmOpen(false);
                  void retireQuestMutation({ variables: { slug: quest.slug } }).then(() => refetch());
                }}
              >
                Leave quest
              </button>
            </div>
          </div>
        ) : null}
        <button
          type="button"
          className="as-bar-minimize"
          aria-label="Minimize the quest bar"
          title="Minimize"
          data-testid="quest-band-minimize"
          onClick={() => {
            setMinimized(true);
            writeMinimized(true);
          }}
        >
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M4 10l4-4 4 4"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="as-bar-trackwrap" ref={trackRef}>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={quest.score}
            aria-labelledby="client-quest-heading"
            className="as-track"
          >
            <div className="as-fill" style={{ width: `${quest.score}%` }} />
            {[25, 50, 75].map((milestone) => (
              <span
                key={milestone}
                className="as-milestone"
                data-passed={quest.score >= milestone ? '1' : '0'}
                style={{ left: `${milestone}%` }}
                aria-hidden="true"
              />
            ))}
          </div>
          <span className="as-bar-standard-tick" aria-hidden="true" />
        </div>

        <div className="as-bar-actions">
          {/* Today is done and another day is still to come: she sees that, not
              tomorrow's work. The locked steps are not in the payload at all — the API
              drops them — so there is nothing here to leak. Mirrors town-square's band. */}
          {quest.isDayComplete && quest.totalDays > 1 && !quest.complete ? (
            <span className="as-bar-action as-bar-action-resting">
              <span className="as-bar-action-stack">
                <span className="as-bar-do-next">Today&rsquo;s steps are done</span>
                <span className="as-bar-action-label">{dayResumeLine(quest.dayUnlocksAt)}</span>
              </span>
            </span>
          ) : nextStep ? (
            <button type="button" className="as-bar-action" onClick={() => openStep(nextStep)}>
              <span className="as-bar-action-stack">
                <span className="as-bar-do-next">Do this next</span>
                <span className="as-bar-action-label">
                  {nextStep.label}
                  {formatStepDuration(nextStep.minutes) ? (
                    <span className="as-bar-mins"> (takes {formatStepDuration(nextStep.minutes)})</span>
                  ) : null}
                </span>
              </span>
              <span className="as-bar-action-arrow" aria-hidden="true">
                →
              </span>
            </button>
          ) : (
            <span />
          )}
          <div className="as-bar-controls">
            <button
              type="button"
              className="as-bar-expand"
              aria-expanded={expanded}
              onClick={() => setExpanded((prev) => !prev)}
            >
              {/* A multi-day quest can legitimately have ONE step open today, and
                  "Show all 1 steps" is what that printed. */}
              {expanded ? 'Hide steps' : `Show all ${quest.steps.length} step${quest.steps.length === 1 ? '' : 's'}`}
              <svg
                className="as-bar-expand-chevron"
                data-open={expanded}
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 6l4 4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <a
              className="as-bar-invite"
              href={`${TOWN_SQUARE}/quests/${quest.slug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Invite a friend
            </a>
          </div>
          <div className="as-bar-footer">
            {quest.totalDays > 1 && !quest.complete ? (
              /* A MULTI-DAY QUEST HAS TO SAY SO WHILE SHE IS MID-DAY (Nick, 2026-09-26).
                 The day count existed only inside the "today is done" panel, so a member
                 working day 1 saw 60% against two steps and nothing telling her the run
                 spans days — it read as one sitting she was two-thirds through. Sits in
                 the footer opposite the points, the corner Nick picked. */
              <span className="as-bar-days">
                <span className="as-bar-days-num">Day {quest.currentDay}</span>
                <span className="as-bar-days-caption">of a {quest.totalDays}-day challenge</span>
              </span>
            ) : null}
            {periodLabel && !quest.complete ? (
              /* A RECURRING QUEST SAYS SO (Nick, 2026-10-08): it comes round again once its
                 period is done, and without this the band read exactly like a one-off. Same
                 corner as the day count, which a recurring quest never has. */
              <span className="as-bar-days" data-testid="quest-band-period">
                <span className="as-bar-days-num">{periodLabel.title}</span>
                {periodLabel.caption ? <span className="as-bar-days-caption">{periodLabel.caption}</span> : null}
              </span>
            ) : null}
            <a
              className={`as-points-block${pointsPulse ? ' is-pulsing' : ''}`}
              data-testid="quest-points-earned"
              href={`${TOWN_SQUARE}/points`}
              target="_blank"
              rel="noopener noreferrer"
              title="See your points activity"
            >
              <span className="as-points-line">
                <span aria-hidden="true">⭐</span>
                <span className="as-points-num">{displayPoints.toLocaleString()}</span>
              </span>
              <span className="as-points-caption">points collected</span>
            </a>
          </div>
        </div>

        {expanded ? (
          <ul className="as-bar-list">
            {quest.steps.map((step) => (
              <li key={step.code} className="as-bar-row">
                <button
                  type="button"
                  className="as-checkbox"
                  role="checkbox"
                  aria-checked={step.complete}
                  aria-label={`Mark "${step.label}" ${step.complete ? 'not done' : 'done'}`}
                  disabled={step.completedBy === 'measured'}
                  title={step.completedBy === 'measured' ? 'Your data shows this — it can’t be unchecked' : undefined}
                  onClick={() => toggleStep(step)}
                >
                  {step.complete ? (
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path
                        d="M3 8.5l3.2 3.2L13 5"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : null}
                </button>
                <button type="button" className="as-bar-row-link" onClick={() => openStep(step)}>
                  <span className="as-bar-row-name">
                    {step.label}
                    {formatStepDuration(step.minutes) ? (
                      <span className="as-bar-mins"> (takes {formatStepDuration(step.minutes)})</span>
                    ) : null}
                  </span>
                  <span className="as-bar-row-count">
                    {step.earned}/{step.weight}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}
